
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface BeautifulMarkdownProps {
    content: string
    className?: string
}

export const BeautifulMarkdown = ({ content, className = '' }: BeautifulMarkdownProps) => {
    return (
        <div className={`prose prose-invert max-w-none text-white ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    code({ inline, className, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(className || '')
                        return !inline && match ? (
                            <SyntaxHighlighter
                                style={oneDark as any}
                                language={match[1]}
                                PreTag="div"
                                className="rounded-xl shadow-md"
                                {...props}
                            >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        ) : (
                            <code className="bg-gray-800/30 px-3 py-1 rounded-lg text-sm font-mono text-orange-200 border border-orange-500/20" {...props}>
                                {children}
                            </code>
                        )
                    },
                    p: ({ children }) => <p className="mb-4 last:mb-0 leading-relaxed text-white">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-2 text-white">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-2 text-white">{children}</ol>,
                    li: ({ children }) => <li className="text-white">{children}</li>,
                    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-bold mb-3 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-bold mb-2 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-base font-bold mb-2 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h4>,
                    h5: ({ children }) => <h5 className="text-sm font-bold mb-2 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h5>,
                    h6: ({ children }) => <h6 className="text-xs font-bold mb-2 bg-gradient-to-r from-orange-400 to-orange-300 bg-clip-text text-transparent">{children}</h6>,
                    blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-orange-400 pl-4 italic my-4 text-gray-200 bg-orange-900/10 py-2 rounded-r-lg">
                            {children}
                        </blockquote>
                    ),
                    strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                    em: ({ children }) => <em className="italic text-white">{children}</em>,
                    span: ({ children }) => <span className="text-white">{children}</span>,
                    table: ({ children }) => (
                        <div className="overflow-x-auto">
                            <table className="min-w-full border-collapse border border-gray-600 mb-4">
                                {children}
                            </table>
                        </div>
                    ),
                    th: ({ children }) => (
                        <th className="border border-gray-600 px-4 py-2 bg-gray-800/50 text-orange-300 font-semibold text-left">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="border border-gray-600 px-4 py-2 text-gray-100">
                            {children}
                        </td>
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    )
}